// src/pages/document/DocumentListPage.tsx
// Notion-style document workspace with sidebar tree and document grid.

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { ErrorAlert, Badge } from '@react/components';
import {
  filterDocuments,
  sortDocumentsByActivity,
  documentFilters,
  documentFilterToStatus,
  documentStatusMap,
  getStatusMapping,
  getDocumentStatus,
  getDocumentOwner,
  getEntityId,
  getInitials,
  getAvatarColor,
  formatRelativeTime,
  type DocumentFilter,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// View modes
// ---------------------------------------------------------------------------

type ViewMode = 'grid' | 'list' | 'table';

function ViewModeSwitch({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const modes: { key: ViewMode; icon: React.ReactNode }[] = [
    {
      key: 'grid',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
        </svg>
      ),
    },
    {
      key: 'list',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      ),
    },
    {
      key: 'table',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
          <line x1="9" y1="3" x2="9" y2="21" />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center border border-border rounded-lg overflow-hidden">
      {modes.map(m => (
        <button
          key={m.key}
          onClick={() => onChange(m.key)}
          className={`p-2 transition-colors ${
            mode === m.key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          title={m.key}
        >
          {m.icon}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search input
// ---------------------------------------------------------------------------

function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  return (
    <div className="relative">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      >
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={t('document.searchPlaceholder')}
        className="w-full pl-10 pr-4 py-2 text-sm bg-transparent border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/30 transition-colors"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Item (Grid Card)
// ---------------------------------------------------------------------------

function DocumentGridCard({ document, locale }: { document: any; locale: string }) {
  const { t } = useI18n();
  if (!document) return null;
  const id = getEntityId(document);
  const status = getDocumentStatus(document);
  const owner = getDocumentOwner(document);
  const statusInfo = getStatusMapping(documentStatusMap, status);

  return (
    <Link
      to={`/documents/${id}`}
      className="group block rounded-xl border border-border overflow-hidden transition-all hover:shadow-md hover:border-primary/20"
    >
      {/* Cover or placeholder */}
      {document.cover_url ? (
        <div className="h-[120px] bg-muted overflow-hidden">
          <img src={document.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        </div>
      ) : (
        <div className="h-[6px] bg-gradient-to-r from-primary/20 to-primary/5" />
      )}

      <div className="p-4">
        {/* Icon + Title */}
        <div className="flex items-start gap-2.5 mb-2">
          {document.icon ? (
            <span className="text-2xl select-none flex-shrink-0 mt-0.5">{document.icon}</span>
          ) : (
            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
              {document.title || t('common.untitled')}
            </h3>
            {document.description && (
              <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{document.description}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            {status && (
              <Badge variant={statusInfo.variant as any} size="sm">
                {t(statusInfo.labelKey)}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {document.updated_at && (
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(document.updated_at, locale)}
              </span>
            )}
            {owner && (
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
                style={{ backgroundColor: getAvatarColor(owner) }}
                title={owner}
              >
                {getInitials(owner)}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Document Item (List row)
// ---------------------------------------------------------------------------

function DocumentListRow({ document, locale }: { document: any; locale: string }) {
  const { t } = useI18n();
  if (!document) return null;
  const id = getEntityId(document);
  const status = getDocumentStatus(document);
  const owner = getDocumentOwner(document);
  const statusInfo = getStatusMapping(documentStatusMap, status);

  return (
    <Link
      to={`/documents/${id}`}
      className="group flex items-center gap-3 px-4 py-3 rounded-lg transition-colors hover:bg-muted/50"
    >
      {document.icon ? (
        <span className="text-lg select-none flex-shrink-0">{document.icon}</span>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground flex-shrink-0">
          <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )}

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate block">
          {document.title || t('common.untitled')}
        </span>
      </div>

      {status && (
        <Badge variant={statusInfo.variant as any} size="sm">
          {t(statusInfo.labelKey)}
        </Badge>
      )}

      {owner && (
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: getAvatarColor(owner) }}
          title={owner}
        >
          {getInitials(owner)}
        </div>
      )}

      {document.updated_at && (
        <span className="text-xs text-muted-foreground w-20 text-right flex-shrink-0 hidden sm:block">
          {formatRelativeTime(document.updated_at, locale)}
        </span>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Document Item (Table row)
// ---------------------------------------------------------------------------

function DocumentTableRow({ document, locale }: { document: any; locale: string }) {
  const { t } = useI18n();
  if (!document) return null;
  const id = getEntityId(document);
  const status = getDocumentStatus(document);
  const owner = getDocumentOwner(document);
  const statusInfo = getStatusMapping(documentStatusMap, status);

  return (
    <Link
      to={`/documents/${id}`}
      className="group contents"
    >
      <td className="py-2.5 px-3 group-hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          {document.icon ? (
            <span className="text-base select-none flex-shrink-0">{document.icon}</span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground flex-shrink-0">
              <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          )}
          <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
            {document.title || t('common.untitled')}
          </span>
        </div>
      </td>
      <td className="py-2.5 px-3 group-hover:bg-muted/50 transition-colors">
        {status && (
          <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>
        )}
      </td>
      <td className="py-2.5 px-3 group-hover:bg-muted/50 transition-colors">
        {owner && (
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
              style={{ backgroundColor: getAvatarColor(owner) }}
            >
              {getInitials(owner)}
            </div>
            <span className="text-sm text-muted-foreground truncate">{owner}</span>
          </div>
        )}
      </td>
      <td className="py-2.5 px-3 group-hover:bg-muted/50 transition-colors text-right">
        <span className="text-xs text-muted-foreground">
          {document.updated_at ? formatRelativeTime(document.updated_at, locale) : ''}
        </span>
      </td>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const DocumentListPage = observer(() => {
  const store = useDocument();
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentFilter>('draft');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [ftsResults, setFtsResults] = useState<any[] | null>(null);
  const [ftsLoading, setFtsLoading] = useState(false);

  const docStatusToNumber: Record<string, number> = {
    DOCUMENT_STATUS_DRAFT: 1,
    DOCUMENT_STATUS_IN_REVIEW: 2,
    DOCUMENT_STATUS_PENDING_APPROVAL: 3,
    DOCUMENT_STATUS_APPROVED: 4,
    DOCUMENT_STATUS_PUBLISHED: 5,
    DOCUMENT_STATUS_REVISION: 6,
    DOCUMENT_STATUS_OBSOLETE: 7,
    DOCUMENT_STATUS_ARCHIVED: 8,
  };

  const fetchDocuments = (filter: DocumentFilter, searchTerm: string) => {
    setIsLoading(true);
    setError(null);

    const filters: Record<string, any> = {};
    const mappedStatus = documentFilterToStatus[filter];
    if (mappedStatus && docStatusToNumber[mappedStatus]) {
      filters.status_filter = docStatusToNumber[mappedStatus];
    }
    if (searchTerm) {
      filters.search_query = searchTerm;
    }

    store.ListDocuments({
      pagination: { cursor: '', limit: 100, direction: 'forward' as const },
      sort: [],
      date_range: {} as any,
      filters: filters as any,
    })
      .then(() => setDocuments(sortDocumentsByActivity(store.items?.filter(Boolean) ?? [])))
      .catch((err: any) => setError(err instanceof Error ? err.message : t('document.loadError')))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchDocuments(statusFilter, search);
  }, [statusFilter]);

  useEffect(() => {
    if (!isLoading) {
      setDocuments(sortDocumentsByActivity(store.items?.filter(Boolean) ?? []));
    }
  }, [store.items]);

  const filtered = useMemo(
    () => filterDocuments(documents, search, 'all'),
    [documents, search],
  );

  // Navigate to create page
  const handleCreate = useCallback(() => {
    navigate('/documents/new');
  }, [navigate]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('document.list')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {!isLoading && (
              <span>{filtered.length} {filtered.length === 1 ? 'document' : 'documents'}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ViewModeSwitch mode={viewMode} onChange={setViewMode} />
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('document.newPage')}
          </button>
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        <button
          type="button"
          disabled={ftsLoading}
          onClick={() => {
            setFtsLoading(true);
            store
              .SearchDocuments()
              .then((res: any) => setFtsResults(res?.results ?? []))
              .catch(() => setFtsResults([]))
              .finally(() => setFtsLoading(false));
          }}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {ftsLoading ? t('common.loading') : t('document.fullTextSearch')}
        </button>
      </div>

      {/* Full-text search results (server-side) */}
      {ftsResults !== null && (
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-foreground">{t('document.ftsResults', { n: ftsResults.length })}</h2>
            <button type="button" onClick={() => setFtsResults(null)} className="text-xs text-muted-foreground hover:text-foreground">{t('common.close')}</button>
          </div>
          {ftsResults.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('document.noSearchResults')}</p>
          ) : (
            <ul className="space-y-1.5">
              {ftsResults.map((r: any, i: number) => (
                <li key={r.document?.entity?.id || i}>
                  <Link to={`/documents/${r.document?.entity?.id}`} className="block rounded-md border border-border p-2.5 hover:bg-muted/40 transition-colors">
                    <div className="text-sm font-medium text-foreground truncate">{r.document?.title || t('common.untitled')}</div>
                    {(r.highlights || []).length > 0 && (
                      <div className="text-xs text-muted-foreground truncate">{r.highlights[0]?.snippet || r.highlights[0]?.text}</div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1 overflow-x-auto">
        {documentFilters.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              statusFilter === f.key
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {/* Error */}
      {!!(error || store.error) && (
        <ErrorAlert error={store.error || error} dismissible onDismiss={() => { setError(null); store.clearError(); }} />
      )}

      {/* Content */}
      {isLoading ? (
        <LoadingSkeleton viewMode={viewMode} />
      ) : filtered.length === 0 ? (
        <EmptyState search={search} onCreate={handleCreate} />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(doc => (
            <DocumentGridCard key={getEntityId(doc)} document={doc} locale={locale} />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-0.5">
          {filtered.map(doc => (
            <DocumentListRow key={getEntityId(doc)} document={doc} locale={locale} />
          ))}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-2.5 px-3">
                  {t('document.fieldTitle')}
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-2.5 px-3 w-[120px]">
                  {t('document.fieldStatus')}
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider py-2.5 px-3 w-[180px]">
                  {t('document.fieldOwner')}
                </th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider py-2.5 px-3 w-[120px]">
                  {t('document.fieldUpdatedAt')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(doc => (
                <tr key={getEntityId(doc)} className="group cursor-pointer" onClick={() => navigate(`/documents/${getEntityId(doc)}`)}>
                  <DocumentTableRow document={doc} locale={locale} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Skeleton & Empty State
// ---------------------------------------------------------------------------

function LoadingSkeleton({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === 'list') {
    return (
      <div className="space-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
            <div className="w-5 h-5 rounded bg-muted flex-shrink-0" />
            <div className="h-4 rounded bg-muted flex-1" style={{ maxWidth: `${40 + Math.random() * 40}%` }} />
            <div className="h-5 w-16 rounded-full bg-muted flex-shrink-0" />
            <div className="h-4 w-12 rounded bg-muted flex-shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border overflow-hidden animate-pulse">
          <div className="h-[6px] bg-muted" />
          <div className="p-4">
            <div className="flex items-start gap-2.5 mb-3">
              <div className="w-7 h-7 rounded-md bg-muted flex-shrink-0" />
              <div className="flex-1">
                <div className="h-4 w-2/3 rounded bg-muted mb-1.5" />
                <div className="h-3 w-1/2 rounded bg-muted" />
              </div>
            </div>
            <div className="flex justify-between mt-3">
              <div className="h-5 w-16 rounded-full bg-muted" />
              <div className="flex items-center gap-2">
                <div className="h-3 w-10 rounded bg-muted" />
                <div className="h-5 w-5 rounded-full bg-muted" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  const { t } = useI18n();

  return (
    <div className="py-20 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-foreground">
        {search ? t('document.noSearchResults') : t('document.noItems')}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
        {search ? t('document.tryDifferentSearch') : t('document.noItemsDescription')}
      </p>
      {!search && (
        <button
          onClick={onCreate}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('document.newPage')}
        </button>
      )}
    </div>
  );
}

export default DocumentListPage;
